import { Box, Flex } from '@chakra-ui/react';
import { CustomSlideIndex, TABS } from '@llx/common';
import { MeetingTypes } from '@llx/common';
import { isEmpty } from 'lodash';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { useLoggedInUserAuthContext } from '@/components/AuthTokenContextProvider/UserAuthContext';
import CaseExplorerClient from '@/components/CaseExplorer/CaseExplorerClient';
import CaseDisclaimerView from '@/components/disclaimer/CaseDisclaimerView';
import LiveMeetingFooter from '@/components/LiveMeetingFooter';
import MeetingRightSideBar, {
  MeetingRightSidebarProps,
} from '@/components/MeetingRightSideBar/MeetingRightSideBar';
import RateMeeting from '@/components/ratecaseCluster/RateMeeting';
import { trackEvent } from '@/features/analytic/analytics';
import AssetPreloader from '@/features/assetPreloader/AssetPreloader';
import useChatMessages from '@/features/call/useChatMessages';
import { useVideoCall } from '@/features/call/useVideoCall';
import { useCurrentMeetingDataContext } from '@/features/meeting/context/CurrentMeetingDataContext';
import { useMeetingStatsCreationContext } from '@/features/meeting-stats/context/MeetingStatsCreationContext';
import PictureInPictureFeature from '@/features/PictureInPictureFeature/PictureInPictureFeature';
import useShowDisclaimer from '@/hooks/useShowDisclaimer';
import { ROUTES } from '@/routes/Routes';
import PageLayout from '@/ui/Layout/PageLayout';
import { PageLoading } from '@/ui/PageLoading';
import { TimerLoader } from '@/ui-components/TimerLoader/TimerLoader';
import useBrowserLeaveCallback from '@/utils/helpers/hooks/useBrowserLeaveCallback';
import { captureSentry } from '@/utils/helpers/sentryHelper';
import { reloadApp } from '@/utils/reloadApp';

import CaseSelectorView from './CaseSelectorView';
import LiveMeetingSider from './Sider/LiveMeetingSider';
import LiveHeader from '../../components/LiveHeader/LiveHeader';
import useNavigateHomeOnMeetingDeletion from '../../hooks/useNavigateHomeOnMeetingDeletion';

/* --------------------- comp ------------------------ */

function LiveMeetingView() {
  const [currentTab, setCurrentTab] = useState(TABS.USERS);
  const { userID, email, isAdmin, currentUser } = useLoggedInUserAuthContext();
  const navigate = useNavigate();
  const { meetingId } = useParams();
  const location = useLocation();
  const { t } = useTranslation();

  const {
    // trackAnswer,
    trackAssetSwitch,
    trackMeetingLeave,
    trackCaseClusterStop,
  } = useMeetingStatsCreationContext();

  const {
    currentMeeting,
    hostFields,
    caseCluster,
    caseMap,
    currentCase,
    currentCaseIndex,
    isCaseLoading,
    isUserHost: isHost,
    meetingInteractions,
    isPreview,
    connectedUserList: participants,
  } = useCurrentMeetingDataContext();

  const [siderCollapsed, setSiderCollapsed] = useState(true);

  const useChatMessageUtils = useChatMessages(currentMeeting.id);
  const [isInPipMode, setIsInPipMode] = useState(false);

  const { leaveCall: leaveVideoCall } = useVideoCall();

  const isNPSScorePage: boolean = hostFields?.currentSlide === CustomSlideIndex.NPS_SCORE || false;

  const {
    showDisclaimer,
    startedAt: disclaimerStartedAt,
    endsAt: disclaimerEndsAt,
  } = useShowDisclaimer({
    currentCase: currentCase,
    isNPSScorePage: isNPSScorePage,
  });

  useNavigateHomeOnMeetingDeletion({
    meetingID: meetingId,
  });

  // --------------------- handlers ------------------------

  const changeTab = (newTab: TABS) => {
    // setSiderCollapsed(!siderCollapsed);
    if (currentTab !== newTab) {
      setCurrentTab(newTab);
    }
    setSiderCollapsed(false);
  };

  // handle meeting restart
  const handleMeetingRestart = () => {
    if (!currentMeeting) {
      captureSentry({
        title: 'handleMeetingRestart: no current meeting',
        detail: {
          currentMeeting,
        },
      });
      return;
    }
    trackEvent('meeting', 'restart');
    void meetingInteractions.restartMeeting(currentMeeting.id);
  };

  const handleMeetingLeave = useCallback(() => {
    leaveVideoCall();
    trackMeetingLeave();
    navigate(ROUTES.HOME, { replace: true });
  }, [leaveVideoCall, trackMeetingLeave, navigate]);

  const closeMeetingSider = () => {
    setSiderCollapsed(true);
  };

  const forceRefreshLiveMeeting = () => {
    captureSentry({
      title: 'Force refresh Live Meeting',
      detail: {
        currentMeeting,
        caseCluster,
        caseMap,
        currentCase,
        currentCaseIndex,
        isCaseLoading,
        isHost,
      },
    });
    reloadApp();
  };

  const meetingRightSidebarProps: MeetingRightSidebarProps = {
    onParticipantsClick: () => {
      if (currentTab === TABS.USERS && !siderCollapsed) {
        closeMeetingSider();
        return;
      }
      changeTab(TABS.USERS);
    },
    onChatClick: () => {
      if (currentTab === TABS.CHAT && !siderCollapsed) {
        closeMeetingSider();
        return;
      }
      changeTab(TABS.CHAT);
      useChatMessageUtils.markAllMessagesAsRead();
    },
    onVideoClick: () => {
      setIsInPipMode(!isInPipMode);
    },
    currentTab: currentTab,
    amtUnreadMessages: useChatMessageUtils.unreadMessages,
  };

  // --------------------- effect ------------------------

  // -> track leaving meeting on Browser leave
  useBrowserLeaveCallback(handleMeetingLeave);

  // --------------------- RENDER ------------------------

  // --> Render Loading
  if (isCaseLoading || !currentMeeting || !caseMap || isEmpty(caseMap)) {
    return <PageLoading />;
  }

  // --------------------- RENDER ------------------------

  return (
    <PageLayout showHeader={false} showFooter={false} bgColor="white">
      {/* HEADER */}
      <LiveHeader
        title={currentMeeting.caseCluster.name}
        subtitle={currentMeeting.name}
        timerKey={hostFields.currentSlide.toString()}
        showRestartButton={currentMeeting.meetingType !== MeetingTypes.LIVE}
        currentCaseIndex={currentCaseIndex}
        userEmail={email}
        enableTimer={isHost}
        isAdmin={isAdmin}
        onRestartClick={handleMeetingRestart}
      />

      {/* CONTENT */}
      <Flex
        flex="auto"
        direction="row"
        overflow="hidden"
        position="relative"
        className="pip-container"
      >
        <>
          {userID && currentCase?.id && !isCaseLoading && !isNPSScorePage && (
            <>
              {currentCase.disclaimer && !!showDisclaimer ? (
                <CaseDisclaimerView
                  disclaimer={currentCase.disclaimer}
                  casePath={currentCase.path}
                />
              ) : (
                <CaseExplorerClient
                  patientCaseIndex={currentCaseIndex}
                  patientCase={currentCase}
                  meeting={currentMeeting}
                  hostFields={hostFields}
                  isHost={isHost}
                  trackAssetSwitch={isPreview ? undefined : trackAssetSwitch}
                />
              )}
            </>
          )}

          {!currentCase && caseCluster && !isNPSScorePage && (
            <CaseSelectorView
              patientCaseIds={caseCluster.cases.edges.map((el) => el.node.id)}
              caseMap={caseMap}
              onCaseSelected={(patientCaseId) => {
                if (isHost) {
                  void meetingInteractions.updateCurrentCase(currentMeeting.id, patientCaseId);
                }
              }}
              shouldOpenAnamneseOnClick={!isHost}
              showTestingModeWarning={currentMeeting?.isInTesting}
              showHostIsPresentingMessage={true}
            />
          )}
          {isNPSScorePage && <RateMeeting isHost={isHost} />}
        </>

        <Box
          h="full"
          position="absolute"
          top="0"
          right={siderCollapsed ? -350 : 0}
          transition="all 0.2s ease-in-out"
        >
          <Box
            display={siderCollapsed ? 'none' : 'block'}
            zIndex={0}
            position="fixed"
            w="100vw"
            h="100vh"
            bg="#ffffff66"
            left="0"
            onClick={closeMeetingSider}
          />
          <LiveMeetingSider
            currentTab={currentTab}
            siderCollapsed={siderCollapsed}
            closeMeetingSider={closeMeetingSider}
            useChatMessageUtils={useChatMessageUtils}
          />

          <MeetingRightSideBar {...meetingRightSidebarProps} />
        </Box>

        <PictureInPictureFeature
          isInPipMode={isInPipMode}
          meeting={currentMeeting}
          user={currentUser}
          isHost={isHost}
          isAdmin={isAdmin}
        />
      </Flex>

      {/* MENU BAR / FOOTER */}
      {currentCase?.disclaimer && showDisclaimer && (
        <TimerLoader startedAt={disclaimerStartedAt} endsAt={disclaimerEndsAt} />
      )}

      <LiveMeetingFooter
        isAdmin={isAdmin}
        isHost={isHost}
        isAllowedToNavigate={(!currentCase?.disclaimer || !showDisclaimer) && !isNPSScorePage}
        leaveWarningTitle={
          isHost && !isNPSScorePage
            ? t('meeting.leave.warn.host.title')
            : t('meeting.leave.warn.title')
        }
        onLeaveMeeting={() => {
          // CASE GO TO NPS
          if (
            isHost &&
            participants.length > 1 &&
            hostFields?.currentSlide !== CustomSlideIndex.NPS_SCORE
          ) {
            trackCaseClusterStop();
            void meetingInteractions.showNPSScoreView(currentMeeting);
          } else {
            handleMeetingLeave();
          }
        }}
        onRefresh={forceRefreshLiveMeeting}
      />

      {caseMap && currentMeeting && currentMeeting.meetingType === MeetingTypes.LIVE && (
        <AssetPreloader caseMap={caseMap} />
      )}
    </PageLayout>
  );
}

export default LiveMeetingView;
